import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { Link, useNavigate } from 'react-router-dom'
import { useToast } from '../components/useToast'
import AuthLayout from '../components/AuthLayout'
import Input from '../components/Input'

export default function ResetPassword() {
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [loading, setLoading] = useState(false)
  const [checking, setChecking] = useState(true)
  const [linkValid, setLinkValid] = useState(false)
  const navigate = useNavigate()
  const toast = useToast()

  // Reglas de contraseña (mismas que el registro)
  const checks = {
    length: password.length >= 8,
    upper: /[A-Z]/.test(password),
    lower: /[a-z]/.test(password),
    number: /[0-9]/.test(password),
    special: /[!@#$%^&*(),.?":{}|<>_\-+=~`[\]/;]/.test(password),
  }
  const passedCount = Object.values(checks).filter(Boolean).length
  const strengthPercent = (passedCount / 5) * 100
  const strengthLabel = passedCount <= 2 ? 'DÉBIL' : passedCount <= 4 ? 'MEDIA' : 'FUERTE'
  const strengthColor = passedCount <= 2 ? '#ef4444' : passedCount <= 4 ? '#eab308' : '#22c55e'
  const allValid = passedCount === 5

  useEffect(() => {
    let cancelled = false

    async function verifyLink() {
      try {
        // 1) Flujo PKCE (Supabase actual): el enlace trae ?code=...
        const params = new URLSearchParams(window.location.search)
        const code = params.get('code')

        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code)
          if (!cancelled) {
            setLinkValid(!error)
            setChecking(false)
          }
          return
        }

        // 2) Flujo implícito (hash #access_token=...&type=recovery)
        // El cliente de Supabase lo procesa automáticamente al cargar.
        const { data: { session } } = await supabase.auth.getSession()
        if (!cancelled) {
          setLinkValid(!!session)
          setChecking(false)
        }
      } catch {
        if (!cancelled) {
          setLinkValid(false)
          setChecking(false)
        }
      }
    }

    // Por si el evento llega un poco después que getSession()
    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' && session) {
        setLinkValid(true)
        setChecking(false)
      }
    })

    verifyLink()

    return () => {
      cancelled = true
      authListener.subscription.unsubscribe()
    }
  }, [])

  async function handleReset() {
    if (!allValid) {
      toast.warning('La contraseña no cumple con todos los requisitos')
      return
    }
    if (password !== confirm) {
      toast.error('Las contraseñas no coinciden')
      return
    }

    setLoading(true)
    const { error } = await supabase.auth.updateUser({ password })
    setLoading(false)

    if (error) {
      toast.error('Error al cambiar la contraseña. El enlace puede haber expirado.')
      return
    }

    toast.success('Contraseña cambiada correctamente')
    navigate('/home')
  }

  // --- Estado: verificando enlace ---
  if (checking) {
    return (
      <AuthLayout>
        <div style={{ textAlign: 'center', padding: '30px 0' }}>
          <p style={{ fontSize: 14, color: 'var(--color-muted)' }}>Verificando enlace...</p>
        </div>
      </AuthLayout>
    )
  }

  // --- Estado: enlace inválido o expirado ---
  if (!linkValid) {
    return (
      <AuthLayout>
        <div style={{ textAlign: 'center', padding: '8px 0' }}>
          <div style={{
            width: 64, height: 64,
            background: 'rgba(239,68,68,0.1)',
            borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 20px',
            fontSize: 28,
          }}>
            ⚠️
          </div>

          <h2 style={{
            fontFamily: 'var(--font-display)',
            fontSize: 22, fontWeight: 600,
            marginBottom: 10, color: 'var(--color-text)',
          }}>
            Enlace inválido o expirado
          </h2>

          <p style={{ fontSize: 14, color: 'var(--color-muted)', lineHeight: 1.6, marginBottom: 24 }}>
            Este enlace de recuperación ya no es válido. Solicita uno nuevo para continuar.
          </p>

          <Link
            to="/forgot-password"
            style={{
              display: 'inline-block',
              padding: '13px 24px',
              background: 'var(--color-primary)',
              color: '#fff', fontSize: 15, fontWeight: 600,
              borderRadius: 'var(--radius-sm)', boxShadow: 'var(--shadow-btn)',
              textDecoration: 'none',
            }}
          >
            Solicitar nuevo enlace
          </Link>
        </div>

        <div style={{
          marginTop: 22, paddingTop: 20,
          borderTop: '1px solid var(--color-border)',
          textAlign: 'center', fontSize: 14, color: 'var(--color-muted)',
        }}>
          <Link to="/login" style={{ fontWeight: 600 }}>← Volver al inicio de sesión</Link>
        </div>
      </AuthLayout>
    )
  }

  // --- Estado: formulario ---
  return (
    <AuthLayout>
      <h2 style={{
        fontFamily: 'var(--font-display)',
        fontSize: 22, fontWeight: 600, marginBottom: 6,
      }}>
        Nueva contraseña
      </h2>
      <p style={{ fontSize: 14, color: 'var(--color-muted)', marginBottom: 24, lineHeight: 1.5 }}>
        Escribe tu nueva contraseña para continuar.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div>
          <Input
            label="Nueva contraseña"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            autoComplete="new-password"
          />

          {/* Barra de fortaleza */}
          <div style={{
            height: 4, borderRadius: 2, background: 'var(--color-border)',
            marginTop: 10, overflow: 'hidden',
          }}>
            <div style={{
              height: '100%', width: `${strengthPercent}%`,
              background: strengthColor, transition: 'width 0.2s, background 0.2s',
            }} />
          </div>
          {password.length > 0 && (
            <div style={{
              textAlign: 'right', fontSize: 11, fontWeight: 700,
              color: strengthColor, marginTop: 4, letterSpacing: 0.5,
            }}>
              {strengthLabel}
            </div>
          )}

          {/* Checklist de requisitos */}
          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <RequirementRow ok={checks.length} label="Mínimo 8 caracteres" />
            <RequirementRow ok={checks.upper} label="Al menos una mayúscula (A-Z)" />
            <RequirementRow ok={checks.lower} label="Al menos una minúscula (a-z)" />
            <RequirementRow ok={checks.number} label="Al menos un número (0-9)" />
            <RequirementRow ok={checks.special} label="Al menos un carácter especial (!@#$%...)" />
          </div>
        </div>

        <Input
          label="Confirmar contraseña"
          type="password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="••••••••"
          autoComplete="new-password"
          error={confirm && password !== confirm ? 'Las contraseñas no coinciden' : ''}
        />

        <button
          onClick={handleReset}
          disabled={loading || !allValid || password !== confirm}
          style={{
            marginTop: 4, padding: '13px',
            background: (loading || !allValid || password !== confirm) ? '#86efac' : 'var(--color-primary)',
            color: '#fff', fontSize: 15, fontWeight: 600,
            borderRadius: 'var(--radius-sm)', boxShadow: 'var(--shadow-btn)',
            border: 'none', cursor: 'pointer',
            transition: 'background var(--transition)',
          }}
        >
          {loading ? 'Cambiando...' : 'Cambiar contraseña →'}
        </button>
      </div>

      <div style={{
        marginTop: 22, paddingTop: 20,
        borderTop: '1px solid var(--color-border)',
        textAlign: 'center', fontSize: 14, color: 'var(--color-muted)',
      }}>
        <Link to="/login" style={{ fontWeight: 600 }}>← Volver al inicio de sesión</Link>
      </div>
    </AuthLayout>
  )
}

function RequirementRow({ ok, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{
        width: 16, height: 16, borderRadius: 4,
        border: ok ? 'none' : '1.5px solid var(--color-border)',
        background: ok ? '#22c55e' : 'transparent',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        {ok && (
          <span style={{ color: '#fff', fontSize: 10, fontWeight: 900, lineHeight: 1 }}>✓</span>
        )}
      </div>
      <span style={{
        fontSize: 13,
        color: ok ? '#22c55e' : 'var(--color-muted)',
      }}>
        {label}
      </span>
    </div>
  )
}